/**
 * Format a phone number with parentheses and dashes
 * 
 * @author Alex Clary
 * 
 * @param {string} value - the value of the field, e.g. '5554443333'
 * @param {string} [name] - the name of the field, e.g. 'primaryPhoneNumber'. Not currently used.
 * 
 * @returns {string} the formatted value of the field, e.g. '(555) 444-3333'. Partial values like '5554' will be partially formatted
 * 
 * @example
 * // returns (555) 444-3333
 * formatPhone('5554443333')
 * @example <caption>Partial phone numbers will be partially formatted (presumably, as the user types them).</caption>
 * // returns (555) 4
 * formatPhone('5554')
 */
export const formatPhone = (value, name) => {
  if (!value) {
    return ''
  }
  const onlyNums = value.replace(/[^\d]/g, '')

  if (onlyNums.length <= 3) {
    return '(' + onlyNums
  }
  if (onlyNums.length <= 6) {
    return '(' + onlyNums.slice(0, 3) + ') ' + onlyNums.slice(3)
  }
  return '(' + onlyNums.slice(0, 3) + ') ' + onlyNums.slice(3, 6) + '-' + onlyNums.slice(6, 10)
}

/**
 * Parses a human-readable phone number to the value we'll save. Essentially the opposite of formatPhone().
 * 
 * @author Alex Clary
 * 
 * @param {string} value - the value of the field, e.g. (555) 444-3333
 * @param {string} [name] - the name of the field, e.g. primaryPhoneNumber. Not currently used.
 * 
 * @returns {string} the unformatted value of the field, e.g. 5554443333
 * 
 * @example
 * // returns '5554443333'
 * formatPhone('(555) 444-3333')
 * @example
 * // returns '5554'
 * formatPhone('(555) 4')
 * @example <caption>If the value has no numeric values, null be returned.</caption>
 * // returns null
 * formatPhone('(')
 */
export const parsePhone = (value, name) => {
  if (!value || value.replace(/[^\d]/g) === '') {
    return null
  }

  return value.replace(/[^\d]/g, '')
}

export const normalizePhone = (value, previousValue, allValues, previousAllValues) => {
  if (!value || value.replace(/[^\d]/g) === '') {
    return null
  }

  return value.replace(/[^\d]/g, '')
}

export const formatDollars = (string) =>
  string && !isNaN(parseFloat(string)) ? parseFloat(string).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ' ―'
           
export const formatAllCaps = (string) =>
  string ? string.toUpperCase() : ''

export const parseYesNoDropdownToBoolean = (value) => {
  return value === 'Yes'
}

export const parseBooleanToYesNoDropdown = (value) => {
  if (value === true) {
    return 'Yes'
  } else if (value === false) {
    return 'No'
  } else {
    return null
  }
}

export const formatLin = str =>
  typeof str !== 'string'
    ? ''
    : str.length === 12
    ? `${str.substring(0, 2)}-${str.substring(2, 4)}-${str.substring(4, 5)}-${str.substring(5)}`
    : str

export const normalizeLin = str => (str ? str.replace(/-/g, '') : str)

// Format date as MM/DD/YYYY
export const formatDate = str =>
  typeof str !== 'string' ? '' : str.length === 8 && !str.includes('/') ? `${str.substr(0, 2)}/${str.substr(2, 2)}/${str.substr(4)}` : str

export const formatSSN = str =>
  typeof str !== 'string' ? '' : str.length === 9 ? `${str.substr(0, 3)}-${str.substr(3, 2)}-${str.substr(5)}` : str

// Does the same thing
export const normalizeSSN = normalizeLin

//Error during 21.11GR into 22.1 suggested by Jaini, Sriram to correct the issue 09-09-2021
export const uploadFileTypeIsValid = filename => {
  const fileExtension = filename && filename.split ? filename.split('.').pop() : ''
  return ['pdf', 'doc', 'docx', 'xlsx', 'xls', 'tif', 'tiff', 'gif', 'jpg', 'jpeg', 'png'].includes(fileExtension)
}

export const snakeToTitle = snake => snake?.split('_')?.map(word => word.length === 1 ? word : word[0] + word.substring(1).toLowerCase())?.join(' ')

export const formatUnixTime = unixTime => {
  const fullDate = new Date(unixTime)

  const finalFormat = ('0' + (fullDate.getMonth() + 1)).slice(-2) + '/' + ('0'+fullDate.getDate()).slice(-2) + '/' + fullDate.getFullYear()
  return finalFormat
}